import { BILLING_STATUS_DISPLAY, TIER_DISPLAY_NAMES, formatStorageBytes } from '@/atoms/subscription.atom';
import { CheckoutDialog } from '@/components/subscription/CheckoutDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { useOrganization } from '@/hooks/useOrganizations';
import { useSubscription } from '@/hooks/useSubscription';
import dayjs from 'dayjs';
import { useState } from 'react';

export const SubscriptionSettings = () => {
  const { subscription, features, loading, initialLoadComplete, error } = useSubscription();
  const organization = useOrganization();
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  if (!initialLoadComplete || loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Subscription</CardTitle>
        </CardHeader>
        <CardContent>
          <div className='space-y-3 animate-pulse'>
            <div className='h-4 w-24 bg-slate-200 dark:bg-stone-800 rounded' />
            <div className='h-6 w-32 bg-slate-200 dark:bg-stone-800 rounded' />
            <div className='h-2 w-full bg-slate-200 dark:bg-stone-800 rounded mt-4' />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Subscription</CardTitle>
          <CardDescription className='text-red-500'>{error}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const storageUsedBytes = organization?.storageUsedBytes || 0;
  const storageLimitBytes = subscription.storageLimitBytes;
  const storagePercentage = storageLimitBytes > 0 ? Math.min((storageUsedBytes / storageLimitBytes) * 100, 100) : 0;
  const billingStatusInfo = BILLING_STATUS_DISPLAY[subscription.billingStatus];

  const getBadgeVariant = (color: string) => {
    switch (color) {
      case 'green':
        return 'success';
      case 'red':
        return 'destructive';
      case 'yellow':
        return 'warning';
      default:
        return 'secondary';
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className='flex items-center justify-between'>
          <div>
            <CardTitle>Subscription</CardTitle>
            <CardDescription>Manage your organization's subscription and billing.</CardDescription>
          </div>
          <Badge variant={getBadgeVariant(billingStatusInfo.color)}>{billingStatusInfo.label}</Badge>
        </div>
      </CardHeader>
      <CardContent className='space-y-5'>
        {/* Plan & billing summary */}
        <div className='flex gap-6'>
          <div className='flex-1'>
            <p className='text-xs font-medium text-slate-500 dark:text-stone-400 uppercase tracking-wide'>Plan</p>
            <p className='text-lg font-semibold text-slate-900 dark:text-white mt-0.5'>
              {TIER_DISPLAY_NAMES[subscription.tier]}
            </p>
          </div>
          {subscription.billingInterval && (
            <div className='flex-1'>
              <p className='text-xs font-medium text-slate-500 dark:text-stone-400 uppercase tracking-wide'>Billing</p>
              <p className='text-lg font-semibold text-slate-900 dark:text-white mt-0.5'>
                {subscription.billingInterval === 'MONTHLY' ? 'Monthly' : 'Annual'}
              </p>
            </div>
          )}
          {subscription.collectionMethod && (
            <div className='flex-1'>
              <p className='text-xs font-medium text-slate-500 dark:text-stone-400 uppercase tracking-wide'>Method</p>
              <p className='text-lg font-semibold text-slate-900 dark:text-white mt-0.5'>
                {subscription.collectionMethod === 'AUTOMATIC' ? 'Card' : 'Invoice'}
              </p>
            </div>
          )}
        </div>

        {/* Next billing date */}
        {subscription.currentPeriodEnd && features.isActive && (
          <div>
            <p className='text-xs font-medium text-slate-500 dark:text-stone-400 uppercase tracking-wide'>
              {subscription.cancelAtPeriodEnd ? 'Cancels on' : 'Next billing'}
            </p>
            <p className='text-lg font-semibold text-slate-900 dark:text-white mt-0.5'>
              {dayjs(subscription.currentPeriodEnd).format('MMM D, YYYY')}
            </p>
          </div>
        )}

        {/* Storage usage */}
        <div className='space-y-2'>
          <div className='flex items-center justify-between'>
            <p className='text-xs font-medium text-slate-500 dark:text-stone-400 uppercase tracking-wide'>Storage</p>
            <p className='text-sm text-slate-700 dark:text-stone-300'>
              {formatStorageBytes(storageUsedBytes)} / {formatStorageBytes(storageLimitBytes)}
            </p>
          </div>
          <Progress value={storagePercentage} className='h-1.5' />
        </div>

        {/* Scheduled changes */}
        {subscription.scheduledTier && subscription.scheduledChangeAt && (
          <div className='text-sm rounded-md bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 px-3 py-2'>
            <span className='text-blue-800 dark:text-blue-200'>
              Plan changing to <span className='font-medium'>{TIER_DISPLAY_NAMES[subscription.scheduledTier]}</span> on{' '}
              {dayjs(subscription.scheduledChangeAt).format('MMM D, YYYY')}
            </span>
          </div>
        )}

        {/* CTA for free users */}
        {features.hasNoSubscription && (
          <Button className='w-full' onClick={() => setCheckoutOpen(true)}>
            Upgrade to a Paid Plan
          </Button>
        )}
      </CardContent>

      <CheckoutDialog open={checkoutOpen} onOpenChange={setCheckoutOpen} />
    </Card>
  );
};
